import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByNodeId, LinkModel, mapLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion from "@insite/mobius/Accordion";
import AccordionSection from "@insite/mobius/AccordionSection";
import Hidden from "@insite/mobius/Hidden";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import UCSColors from "@ucs/Utils/UCSColors";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    direction = "direction",
    alignment = "alignment",
    title = "title",
    links = "links",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.direction]: "vertical" | "horizontal";
        [fields.alignment]: "left" | "center" | "right";
        [fields.title]: string;
        [fields.links]: LinkModel[];
    };
    extendedStyles?: LinkListStyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const links = mapLinks<LinkModel, { openInNewWindow: boolean; overriddenTitle: string }>(
        state,
        ownProps.fields.links,
        widgetLink => ({
            openInNewWindow: widgetLink.fields.openInNewWindow,
            overriddenTitle: widgetLink.fields.overriddenTitle,
        }),
    );
    return {
        links,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface LinkListStyles {
    linkListWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    linkWrapper?: InjectableCss;
    accordionHeader?: InjectableCss;
}

export const linkListStyles: LinkListStyles = {
    linkListWrapper: {
        css: css`
            color: ${UCSColors.gray10};
        `,
    },
    titleWrapper: {
        css: css`
            font-weight: 500;
            font-size: 18px;
        `,
    },
    linkWrapper: {
        css: css`
            font-size: 16px;
            padding: 1px 18px 0 0;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        margin-top: 8px;
                    `,
                    css`
                        margin-top: 8px;
                    `,
                    css`
                        margin-top: 16px;
                    `,
                    css`
                        margin-top: 16px;
                    `,
                    css`
                        margin-top: 16px;
                    `,
                ]);
            }}
        `,
    },
    accordionHeader: {
        css: css`
            && dt,
            && dd,
            && button,
            && span {
                border-bottom: none;
                border-right: none;
                border-left: none;
                border-color: ${UCSColors.gray03};
                background: transparent;
                padding-left: 0;

                &:focus {
                    padding-left: 0;
                    border-top: 1px solid ${UCSColors.gray03};
                    border-bottom: none;
                    border-right: none;
                    border-left: none;
                    border-color: ${UCSColors.gray03};
                }
            }
            && svg {
                color: ${UCSColors.gray10};
            }
            && dd {
                padding-top: 0;
                padding-left: 20px;
            }
            && button {
                padding-top: 13px;
                &:focus {
                    padding-top: 13px;
                }
            }
        `,
    },
};

const FooterLinkList: React.FC<Props> = ({ fields, links, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(linkListStyles, extendedStyles));

    const alignmentStyles = css`
        text-align: ${fields.alignment || "right"};
        ${styles.linkListWrapper?.css}
    `;

    const directionStyles = css`
        ${fields.direction === "horizontal" ? "display: inline-block;" : ""};
        ${styles.linkWrapper?.css}
    `;

    const accordionStyles = css`
        ${styles.accordionHeader?.css}
    `;

    return (
        <StyledWrapper css={alignmentStyles}>
            <Hidden below="sm">
                <StyledWrapper {...styles.titleWrapper}>
                    <Typography as="h3" {...styles.title}>
                        {fields.title}
                    </Typography>
                </StyledWrapper>
                {links.map((link, index) => (
                    <StyledWrapper key={index} css={directionStyles}>
                        {link?.url && (
                            <Link
                                href={link.url}
                                target={link.openInNewWindow ? "_blank" : ""}
                                color={UCSColors.gray08}
                                hoverMode="lighten"
                            >
                                {link.overriddenTitle || link.title}
                            </Link>
                        )}
                    </StyledWrapper>
                ))}
            </Hidden>
            <Hidden above="xs" id={fields.title}>
                <Accordion headingLevel={6} css={accordionStyles}>
                    <AccordionSection
                        title={fields.title}
                        titleTypographyProps={{ color: `${UCSColors.gray10}`, size: 18, weight: 600 }}
                    >
                        {links.map((link, index) => (
                            <StyledWrapper key={index} css={directionStyles}>
                                {link?.url && (
                                    <Link
                                        href={link.url}
                                        target={link.openInNewWindow ? "_blank" : ""}
                                        color={UCSColors.gray08}
                                        style={{ fontSize: 16, fontWeight: 300, color: `${UCSColors.gray08}` }}
                                    >
                                        {link.overriddenTitle || link.title}
                                    </Link>
                                )}
                            </StyledWrapper>
                        ))}
                    </AccordionSection>
                </Accordion>
            </Hidden>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(FooterLinkList),
    definition: {
        group: "Footer",
        fieldDefinitions: [
            {
                name: fields.direction,
                editorTemplate: "DropDownField",
                defaultValue: "vertical",
                fieldType: "General",
                options: [
                    {
                        displayName: "Vertical",
                        value: "vertical",
                    },
                    {
                        displayName: "Horizontal",
                        value: "horizontal",
                    },
                ],
                hideEmptyOption: true,
            },
            {
                name: fields.alignment,
                editorTemplate: "DropDownField",
                defaultValue: "left",
                fieldType: "General",
                options: [
                    {
                        displayName: "Left",
                        value: "left",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Right",
                        value: "right",
                    },
                ],
                hideEmptyOption: true,
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        return value; // TODO ISC-10781 make this work
                        // const category = getLink(state, { type: "Category", value }); //need ApplicationState
                        // return category?.title;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
